import { useEffect, useMemo, useState } from "react";
import { Lock, ShieldAlert, X, Upload, FileText, Crosshair, Radio } from "lucide-react";
import Input from "./common/Input";
import Select from "./common/Select";
import { getSavedTopologies } from "../api/services/topologyService";
import ATTACK_DATA from "../data/AttackData"

/* ------------------------------------------------------------------ */
/*  Static option sets                                                 */
/* ------------------------------------------------------------------ */

const FRAMEWORK_OPTIONS = [
  { value: "", label: "Select a framework" },
  { value: "NICE", label: "NICE" },
  { value: "Scwyf", label: "Scwyf" },
  { value: "ECSF", label: "ECSF" },
];

// Domain of Operations — Purple & Governance are "grey" (locked)
const DOMAIN_OPTIONS = [
  { value: "red", label: "Red", locked: false },
  { value: "blue", label: "Blue", locked: false },
  { value: "purple", label: "Purple", locked: true },
  { value: "grc", label: "GRC", locked: true },
  { value: "strategictabletop", label: "Strategic and Tabletop Exercises", locked: true },
];

// Creation strategy tabs
const STRATEGIES = [
  { value: "cve", title: "CVE", desc: "By vulnerability", locked: false },
  { value: "ttp", title: "TTPs", desc: "ATT&CK mapping", locked: false },
  {
    value: "vuln_theme",
    title: "Description",
    desc: "Free-form story",
    locked: false,
  },
  { value: "file", title: "File / Doc", desc: "Upload source", locked: true },
];

// Threat emulation — Attack Injector sub-options (locked for now)
const ATTACK_INJECTOR_SUBS = [
  { value: "phased", label: "Phased / Stepwise Attack Execution", locked: true },
  { value: "continuous", label: "Uninterrupted / Continuous Emulation", locked: true },
];

// Threat emulation — Noise Injector sub-options (locked for now)
const NOISE_INJECTOR_SUBS = [
  { value: "benign", label: "Benign / Legitimate Activity & Events", locked: true },
  { value: "unrelated", label: "Unrelated Attacks", locked: true },
];

// Controls — IDS, SOAR, EDR are "grey" (locked)
const CONTROL_OPTIONS = [
  { value: "firewall", label: "Firewall", locked: false },
  { value: "siem", label: "SIEM", locked: false },
  { value: "waf", label: "WAF", locked: false },
  { value: "ids", label: "IDS", locked: true },
  { value: "soar", label: "SOAR", locked: true },
  { value: "edr", label: "EDR / NDR / XDR", locked: true },
];

const DIFFICULTY_OPTIONS = [
  { value: "easy", main: "Easy", alt: "Novice / Beginner" },
  { value: "medium", main: "Medium", alt: "Intermediate / Practitioner" },
  { value: "hard", main: "Hard", alt: "Advanced / Pro" },
  { value: "legendary", main: "Legendary", alt: "Ultimate" },
];

const DURATION_OPTIONS = [
  { value: "15", val: "15", unit: "mins" },
  { value: "30", val: "30", unit: "mins" },
  { value: "45", val: "45", unit: "mins" },
  { value: "60", val: "60", unit: "mins" },
  { value: "60plus", val: ">60", unit: "mins" },
];

const MODALITY_OPTIONS = [
  { value: "single", label: "Single Player" },
  { value: "team", label: "Team-based" },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function normalizeSavedTopologiesList(savedRes) {
  if (Array.isArray(savedRes?.saved_topologies))
    return savedRes.saved_topologies;
  if (Array.isArray(savedRes?.topologies)) return savedRes.topologies;
  if (Array.isArray(savedRes)) return savedRes;
  return [];
}

function LockedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-(--color-border) bg-(--color-bg-card)/90 px-1.5 py-0.5 text-[10px] font-medium text-(--color-text-muted) backdrop-blur-sm">
      <Lock className="h-2.5 w-2.5" />
    </span>
  );
}

function SectionHeading({ children, hint }) {
  return (
    <div className="mb-3 flex items-baseline justify-between border-t border-dashed border-(--color-border) pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-(--color-primary)/90">
        {children}
      </h4>
      {hint ? (
        <span className="text-[11px] text-(--color-text-muted)">{hint}</span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function BuildYourOwnScenarioModal({ open, onClose, onSubmit }) {
  // Identity
  const [domain, setDomain] = useState("");

  // Strategy
  const [strategy, setStrategy] = useState("cve");
  const [cve, setCve] = useState("");
  const [story, setStory] = useState("");
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);

  // TTP cascade
  const [tactic, setTactic] = useState("");
  const [technique, setTechnique] = useState("");
  const [subtechnique, setSubtechnique] = useState("");
  const [procedure, setProcedure] = useState("");

  // Threat emulation
  const [attackInjector, setAttackInjector] = useState(false);
  const [noiseInjector, setNoiseInjector] = useState(false);

  // Controls
  const [controlsMode, setControlsMode] = useState("none"); // 'controls' | 'none'
  const [controls, setControls] = useState([]);

  // Miscellaneous
  const [difficulty, setDifficulty] = useState("");
  const [duration, setDuration] = useState("");
  const [modality, setModality] = useState("");
  const [framework, setFramework] = useState("");

  // Topologies
  const [selectedTopologyId, setSelectedTopologyId] = useState("");
  const [savedTopologies, setSavedTopologies] = useState([]);
  const [isLoadingTopologies, setIsLoadingTopologies] = useState(false);
  const [topologyLoadError, setTopologyLoadError] = useState("");

  // Submit
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---- Esc to close ---- */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  /* ---- Reset on close ---- */
  useEffect(() => {
    if (!open) {
      setDomain("");
      setStrategy("cve");
      setCve("");
      setStory("");
      setFileName("");
      setFile(null);
      setTactic("");
      setTechnique("");
      setSubtechnique("");
      setProcedure("");
      setAttackInjector(false);
      setNoiseInjector(false);
      setControlsMode("none");
      setControls([]);
      setDifficulty("");
      setDuration("");
      setModality("");
      setFramework("");
      setSelectedTopologyId("");
      setSavedTopologies([]);
      setTopologyLoadError("");
      setSubmitError("");
    }
  }, [open]);

  /* ---- Load saved topologies ---- */
  useEffect(() => {
    if (!open) return;
    let isActive = true;

    const loadSavedTopologies = async () => {
      try {
        setIsLoadingTopologies(true);
        setTopologyLoadError("");
        const response = await getSavedTopologies();
        if (!isActive) return;
        setSavedTopologies(normalizeSavedTopologiesList(response));
      } catch (error) {
        if (!isActive) return;
        setSavedTopologies([]);
        setTopologyLoadError(
          error?.message || "Failed to load saved topologies.",
        );
      } finally {
        if (isActive) setIsLoadingTopologies(false);
      }
    };

    loadSavedTopologies();
    return () => {
      isActive = false;
    };
  }, [open]);

  const topologyOptions = useMemo(() => {
    const base = [{ value: "", label: "Select a saved topology" }];
    const mapped = savedTopologies.map((item, index) => ({
      value: String(item.id ?? item.topology_id ?? item.thread_id ?? index),
      label: item.name ?? item.title ?? `Saved Topology ${index + 1}`,
    }));
    return [...base, ...mapped];
  }, [savedTopologies]);

  /* ---- TTP cascade derived option lists ---- */
  const tacticOptions = useMemo(
    () => [
      { value: "", label: "Select tactic…" },
      ...ATTACK_DATA.tactics.map((t) => ({
        value: t.id,
        label: `${t.id} — ${t.name}`,
      })),
    ],
    [],
  );

  const techniqueOptions = useMemo(() => {
    if (!tactic || !ATTACK_DATA.techniques[tactic]) {
      return [{ value: "", label: "Select a tactic first…" }];
    }
    return [
      { value: "", label: "Select technique…" },
      ...ATTACK_DATA.techniques[tactic].map((t) => ({
        value: t.id,
        label: `${t.id} — ${t.name}`,
      })),
    ];
  }, [tactic]);

  const subtechniqueOptions = useMemo(() => {
    const subs = technique ? ATTACK_DATA.subs[technique] : null;
    if (!technique) {
      return [{ value: "", label: "Select a technique first…" }];
    }
    if (!subs || !subs.length) {
      return [{ value: "", label: "No sub-techniques" }];
    }
    return [
      { value: "", label: "None / not applicable…" },
      ...subs.map((s) => ({ value: s.id, label: `${s.id} — ${s.name}` })),
    ];
  }, [technique]);

  const procedureOptions = useMemo(() => {
    const target = subtechnique || technique;
    const procs = target ? ATTACK_DATA.procs[target] : null;
    if (!technique) {
      return [{ value: "", label: "Select a technique first…" }];
    }
    if (!procs || !procs.length) {
      return [{ value: "", label: "No procedure examples" }];
    }
    return [
      { value: "", label: "Select procedure example…" },
      ...procs.map((p) => ({ value: p, label: p })),
    ];
  }, [technique, subtechnique]);

  /* ---- Cascade resets ---- */
  const handleTacticChange = (value) => {
    setTactic(value);
    setTechnique("");
    setSubtechnique("");
    setProcedure("");
  };
  const handleTechniqueChange = (value) => {
    setTechnique(value);
    setSubtechnique("");
    setProcedure("");
  };
  const handleSubtechniqueChange = (value) => {
    setSubtechnique(value);
    setProcedure("");
  };

  /* ---- Control toggles ---- */
  const toggleControl = (value) => {
    setControls((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );
  };

  const handleFileChange = (event) => {
    const f = event.target.files?.[0] ?? null;
    setFile(f);
    setFileName(f ? f.name : "");
  };

  if (!open) return null;

  /* ---- Submit ---- */
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Require the active strategy to have its content
    if (strategy === "cve" && !cve.trim()) return;
    if (strategy === "vuln_theme" && !story.trim()) return;
    if (strategy === "ttp" && !technique) return;
    if (strategy === "file" && !file) return;

    const selectedTopology = savedTopologies.find((item, index) => {
      const itemId = String(
        item.id ?? item.topology_id ?? item.thread_id ?? index,
      );
      return itemId === selectedTopologyId;
    });
    const topologyId = selectedTopology
      ? String(
          selectedTopology.id ??
            selectedTopology.topology_id ??
            selectedTopology.thread_id ??
            selectedTopologyId,
        )
      : selectedTopologyId;

    setIsSubmitting(true);
    setSubmitError("");

    // ---- Build a human-readable TTP string (if TTP strategy) ----
    const ttpParts = [tactic, technique, subtechnique, procedure].filter(
      Boolean,
    );
    const ttpString = ttpParts.join(" / ");

    // ---- Derive cveTitle + description for the shared API handler ----
    // handleSubmitCustomRequest expects { cveTitle, description, framework, topology_id }.
    // We map whichever strategy is active into those two text fields, then attach
    // all the rich Build-Your-Own selections under `details` so nothing is lost.
    let cveTitle = "";
    let description = "";

    switch (strategy) {
      case "cve":
        cveTitle = cve.trim();
        description = `[CVE] ${cve.trim()}`;
        break;
      case "ttp":
        cveTitle = ttpString || "TTP scenario";
        description = `[TTPs] ${ttpString || "none selected"}`;
        break;
      case "vuln_theme":
        cveTitle = story.trim().slice(0, 80);
        description = story.trim();
        break;
      case "file":
        cveTitle = fileName || "File-based scenario";
        description = `[File] ${fileName || "no file selected"}`;
        break;
      default:
        description = "";
    }

    // Append the rich context so the backend / reviewer sees the full picture
    const contextLines = [
      domain ? `Domain: ${domain}` : null,
      difficulty ? `Difficulty: ${difficulty}` : null,
      duration ? `Duration: ${duration}` : null,
      modality ? `Modality: ${modality}` : null,
      `Attack Injector: ${attackInjector ? "on" : "off"}`,
      `Noise Injector: ${noiseInjector ? "on" : "off"}`,
      controlsMode === "controls"
        ? `Controls: ${controls.join(", ") || "none"}`
        : "Controls: none",
    ].filter(Boolean);

    const fullDescription = [description, "", ...contextLines]
      .join("\n")
      .trim();

    // Payload handed to the shared onSubmit (handleSubmitCustomRequest)
    setIsSubmitting(true);
    setSubmitError("");

    const submitPayload = {
      domain,
      strategy,
      cve: strategy === "cve" ? cve.trim() : null,
      vuln_theme: strategy === "vuln_theme" ? story.trim() : "",
      ttp:
        strategy === "ttp"
          ? { tactic, technique, subtechnique, procedure }
          : {},
      noise: noiseInjector,
      controls: controlsMode === "controls" ? controls : [],
      difficulty,
      duration,
      framework: framework.trim(),
      topology_id: topologyId || null,
    };

    // console.log("[Build Your Own] submitting:", submitPayload);

    try {
      await onSubmit?.(submitPayload);
      onClose();
    } catch (error) {
      setSubmitError(error?.message || "Failed to capture request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------- */

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/75 px-4 py-8 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-200 flex-col rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_24px_60px_rgba(0,0,0,0.28)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-(--color-primary)/20 bg-(--color-primary)/10">
              <ShieldAlert className="h-5 w-5 text-(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-(--color-text-primary)">
                Build Your Own Scenario
              </h3>
              <p className="text-xs text-(--color-text-muted)">
                Define the domain, attack strategy, environment, and controls.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
            aria-label="Close"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form
          className="flex-1 space-y-5 overflow-y-auto px-6 py-6"
          onSubmit={handleSubmit}
        >
          {/* ── Domain of Operations ── */}
          <div>
            <SectionHeading hint="Pick the team perspective">
              Domain of Operations
            </SectionHeading>
            <div className="flex flex-wrap gap-2">
              {DOMAIN_OPTIONS.map((opt) => {
                const active = domain === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.locked}
                    onClick={() => !opt.locked && setDomain(opt.value)}
                    className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-sm font-medium transition ${
                      opt.locked
                        ? "cursor-not-allowed border-(--color-border) text-(--color-text-muted) opacity-60"
                        : active
                          ? "border-(--color-primary) bg-(--color-primary)/10 text-(--color-text-primary) shadow-[0_0_12px_var(--color-shadow-primary)]"
                          : "border-(--color-border) text-(--color-text-secondary) hover:border-(--color-primary)/60"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        active && !opt.locked
                          ? "bg-(--color-primary)"
                          : "bg-(--color-border)"
                      }`}
                    />
                    {opt.label}
                    {opt.locked ? <LockedBadge /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Creation Strategy ── */}
          <div>
            <SectionHeading hint="Choose how the scenario is seeded">
              Creation Strategy
            </SectionHeading>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {STRATEGIES.map((s) => {
                const active = strategy === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={s.locked}
                    onClick={() => !s.locked && setStrategy(s.value)}
                    className={`rounded-sm border px-3 py-3 text-center transition ${
                      s.locked
                        ? "cursor-not-allowed border-(--color-border) opacity-60"
                        : active
                          ? "border-(--color-primary) bg-(--color-primary)/10"
                          : "border-(--color-border) hover:border-(--color-primary)/60"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center gap-1.5 text-sm font-semibold ${
                        s.locked
                          ? "text-(--color-text-muted)"
                          : active
                            ? "text-(--color-text-primary)"
                            : "text-(--color-text-secondary)"
                      }`}
                    >
                      {s.title}
                      {s.locked ? <LockedBadge /> : null}
                    </div>
                    <div className="mt-0.5 text-[11px] text-(--color-text-muted)">
                      {s.desc}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Strategy panels */}
            <div className="mt-4">
              {strategy === "cve" && (
                <Input
                  label="CVE ID"
                  name="cve"
                  value={cve}
                  onChange={(event) => setCve(event.target.value)}
                  placeholder="CVE-2024-XXXXX"
                />
              )}

              {strategy === "ttp" && (
                <div className="space-y-4">
                  <p className="text-xs text-(--color-text-muted)">
                    Pick a tactic, then drill down. Techniques load from the
                    tactic; sub-techniques and procedures load from the
                    technique. Source: MITRE ATT&amp;CK Enterprise.
                  </p>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Select
                      label="Tactic"
                      name="tactic"
                      value={tactic}
                      onChange={(e) => handleTacticChange(e.target.value)}
                      options={tacticOptions}
                    />
                    <Select
                      label="Technique"
                      name="technique"
                      value={technique}
                      onChange={(e) => handleTechniqueChange(e.target.value)}
                      options={techniqueOptions}
                      disabled={!tactic}
                    />
                    <Select
                      label="Sub-technique (optional)"
                      name="subtechnique"
                      value={subtechnique}
                      onChange={(e) => handleSubtechniqueChange(e.target.value)}
                      options={subtechniqueOptions}
                      disabled={
                        !technique ||
                        !ATTACK_DATA.subs[technique] ||
                        !ATTACK_DATA.subs[technique].length
                      }
                    />
                    <Select
                      label="Procedure (example actor)"
                      name="procedure"
                      value={procedure}
                      onChange={(e) => setProcedure(e.target.value)}
                      options={procedureOptions}
                      disabled={!technique}
                    />
                  </div>

                  {(tactic || technique) && (
                    <div className="flex flex-wrap gap-2">
                      {tactic && (
                        <span className="rounded-sm border border-(--color-primary)/30 bg-(--color-primary)/10 px-2.5 py-1 font-mono text-xs text-(--color-text-primary)">
                          <b className="text-(--color-primary)">Tactic</b>{" "}
                          {tactic}
                        </span>
                      )}
                      {technique && (
                        <span className="rounded-sm border border-(--color-primary)/30 bg-(--color-primary)/10 px-2.5 py-1 font-mono text-xs text-(--color-text-primary)">
                          <b className="text-(--color-primary)">Technique</b>{" "}
                          {technique}
                        </span>
                      )}
                      {subtechnique && (
                        <span className="rounded-sm border border-(--color-primary)/30 bg-(--color-primary)/10 px-2.5 py-1 font-mono text-xs text-(--color-text-primary)">
                          <b className="text-(--color-primary)">Sub</b>{" "}
                          {subtechnique}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {strategy === "vuln_theme" && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                    Description / Story
                  </label>
                  <textarea
                    rows={4}
                    value={story}
                    onChange={(event) => setStory(event.target.value)}
                    placeholder="Describe the scenario narrative — the actor, the target, the chain of events the player will encounter…"
                    className="w-full rounded-sm border-2 border-(--color-border) bg-(--color-bg-card) px-4 py-3 text-sm text-(--color-text-primary) placeholder-(--color-text-muted) focus:border-(--color-primary) focus:shadow-[0_0_8px_var(--color-shadow-primary)] focus:outline-none"
                  />
                </div>
              )}

              {strategy === "file" && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                    File / Doc Upload
                  </label>
                  <label
                    htmlFor="byos-file"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-(--color-border) bg-(--color-bg-card) px-6 py-8 text-center transition hover:border-(--color-primary)/60"
                  >
                    <Upload className="h-6 w-6 text-(--color-primary)" />
                    <span className="text-sm font-medium text-(--color-text-primary)">
                      Upload a file or document
                    </span>
                    <span className="text-xs text-(--color-text-muted)">
                      Threat report, playbook, or scenario spec — PDF, DOCX, MD
                    </span>
                    <input
                      id="byos-file"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    {fileName ? (
                      <span className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-(--color-primary)">
                        <FileText className="h-3.5 w-3.5" />
                        {fileName}
                      </span>
                    ) : null}
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* ── Threat Emulation ── */}
          <div>
            <SectionHeading hint="Environment realism">
              Threat Emulation
            </SectionHeading>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* ── Attack Injector Card ── */}
              <div
                className={`rounded-sm border transition-all duration-200 ${
                  attackInjector
                    ? "border-(--color-primary) shadow-[0_0_16px_var(--color-shadow-primary)]"
                    : "border-(--color-border)"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setAttackInjector((prev) => !prev)}
                  className={`flex w-full items-start gap-3 rounded-sm px-4 py-3.5 text-left transition ${
                    attackInjector
                      ? "bg-(--color-primary)/10"
                      : "hover:bg-(--color-primary)/5"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border transition ${
                      attackInjector
                        ? "border-(--color-primary)/40 bg-(--color-primary)/15"
                        : "border-(--color-border) bg-(--color-bg-card)"
                    }`}
                  >
                    <Crosshair
                      className={`h-4 w-4 transition ${
                        attackInjector
                          ? "text-(--color-primary)"
                          : "text-(--color-text-muted)"
                      }`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${
                          attackInjector
                            ? "text-(--color-text-primary)"
                            : "text-(--color-text-secondary)"
                        }`}
                      >
                        Attack Injector
                      </span>
                      <span
                        className={`h-1.5 w-1.5 rounded-full transition ${
                          attackInjector
                            ? "bg-(--color-primary)"
                            : "bg-(--color-border)"
                        }`}
                      />
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-(--color-text-muted)">
                      Primary attack sequence execution mode
                    </p>
                  </div>
                </button>

                {attackInjector && (
                  <div className="border-t border-(--color-primary)/20 px-4 py-3 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-(--color-primary)/70">
                      Execution Mode
                    </p>
                    {ATTACK_INJECTOR_SUBS.map((sub) => (
                      <div
                        key={sub.value}
                        className="flex cursor-not-allowed items-center justify-between rounded-sm border border-(--color-border)/60 bg-(--color-bg-card)/50 px-3 py-2 opacity-50"
                      >
                        <span className="text-xs text-(--color-text-muted)">
                          {sub.label}
                        </span>
                        <LockedBadge />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Noise Injector Card ── */}
              <div
                className={`rounded-sm border transition-all duration-200 ${
                  noiseInjector
                    ? "border-(--color-primary) shadow-[0_0_16px_var(--color-shadow-primary)]"
                    : "border-(--color-border)"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setNoiseInjector((prev) => !prev)}
                  className={`flex w-full items-start gap-3 rounded-sm px-4 py-3.5 text-left transition ${
                    noiseInjector
                      ? "bg-(--color-primary)/10"
                      : "hover:bg-(--color-primary)/5"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border transition ${
                      noiseInjector
                        ? "border-(--color-primary)/40 bg-(--color-primary)/15"
                        : "border-(--color-border) bg-(--color-bg-card)"
                    }`}
                  >
                    <Radio
                      className={`h-4 w-4 transition ${
                        noiseInjector
                          ? "text-(--color-primary)"
                          : "text-(--color-text-muted)"
                      }`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${
                          noiseInjector
                            ? "text-(--color-text-primary)"
                            : "text-(--color-text-secondary)"
                        }`}
                      >
                        Noise Injector
                      </span>
                      <span
                        className={`h-1.5 w-1.5 rounded-full transition ${
                          noiseInjector
                            ? "bg-(--color-primary)"
                            : "bg-(--color-border)"
                        }`}
                      />
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-(--color-text-muted)">
                      Background noise &amp; decoy event generation
                    </p>
                  </div>
                </button>

                {noiseInjector && (
                  <div className="border-t border-(--color-primary)/20 px-4 py-3 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-(--color-primary)/70">
                      Noise Composition
                    </p>
                    {NOISE_INJECTOR_SUBS.map((sub) => (
                      <div
                        key={sub.value}
                        className="flex cursor-not-allowed items-center justify-between rounded-sm border border-(--color-border)/60 bg-(--color-bg-card)/50 px-3 py-2 opacity-50"
                      >
                        <span className="text-xs text-(--color-text-muted)">
                          {sub.label}
                        </span>
                        <LockedBadge />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Controls ── */}
          <div>
            <SectionHeading hint="Defensive stack in play">
              Controls
            </SectionHeading>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "controls", label: "Controls" },
                { value: "none", label: "No Controls" },
              ].map((opt) => {
                const active = controlsMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setControlsMode(opt.value)}
                    className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-sm font-medium transition ${
                      active
                        ? "border-(--color-primary) bg-(--color-primary)/10 text-(--color-text-primary) shadow-[0_0_12px_var(--color-shadow-primary)]"
                        : "border-(--color-border) text-(--color-text-secondary) hover:border-(--color-primary)/60"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        active ? "bg-(--color-primary)" : "bg-(--color-border)"
                      }`}
                    />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {controlsMode === "controls" && (
              <div className="mt-3 space-y-2 rounded-sm border-l-2 border-(--color-primary) bg-(--color-primary)/5 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-(--color-primary)">
                  Active controls
                </p>
                <div className="flex flex-wrap gap-2">
                  {CONTROL_OPTIONS.map((c) => {
                    const checked = controls.includes(c.value);
                    return (
                      <label
                        key={c.value}
                        className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition ${
                          c.locked
                            ? "cursor-not-allowed border-(--color-border) text-(--color-text-muted) opacity-60"
                            : "cursor-pointer border-(--color-border) text-(--color-text-secondary) hover:border-(--color-primary)/60"
                        } ${
                          checked && !c.locked
                            ? "border-(--color-primary) bg-(--color-primary)/10 text-(--color-text-primary)"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={c.locked}
                          checked={checked}
                          onChange={() => !c.locked && toggleControl(c.value)}
                          className="h-3 w-3 accent-(--color-primary)"
                        />
                        {c.label}
                        {c.locked ? <LockedBadge /> : null}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Miscellaneous ── */}
          <div>
            <SectionHeading hint="Difficulty, duration & modality">
              Miscellaneous
            </SectionHeading>

            {/* Difficulty */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                Suggested Difficulty
              </label>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {DIFFICULTY_OPTIONS.map((d) => {
                  const active = difficulty === d.value;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => setDifficulty(d.value)}
                      className={`rounded-sm border px-3 py-3 text-left transition ${
                        active
                          ? "border-(--color-primary) bg-(--color-primary)/10"
                          : "border-(--color-border) hover:border-(--color-primary)/60"
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${
                          active
                            ? "text-(--color-text-primary)"
                            : "text-(--color-text-secondary)"
                        }`}
                      >
                        {d.main}
                      </div>
                      <div className="mt-0.5 text-[11px] text-(--color-text-muted)">
                        {d.alt}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Duration */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                Suggested Duration
              </label>
              <div className="grid grid-cols-3 gap-2 md:grid-cols-5">
                {DURATION_OPTIONS.map((d) => {
                  const active = duration === d.value;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => setDuration(d.value)}
                      className={`flex flex-col items-center justify-center rounded-sm border px-2 py-3 transition ${
                        active
                          ? "border-(--color-primary) bg-(--color-primary)/10"
                          : "border-(--color-border) hover:border-(--color-primary)/60"
                      }`}
                    >
                      <span
                        className={`font-mono text-lg font-semibold ${
                          active
                            ? "text-(--color-text-primary)"
                            : "text-(--color-text-secondary)"
                        }`}
                      >
                        {d.val}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-(--color-text-muted)">
                        {d.unit}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Modality + Framework */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-(--color-text-secondary)">
                  Play Modality
                  <LockedBadge />
                </label>
                <div className="flex gap-2">
                  {MODALITY_OPTIONS.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      disabled
                      className="flex-1 cursor-not-allowed rounded-sm border border-(--color-border) px-4 py-2.5 text-sm font-medium text-(--color-text-muted) opacity-60"
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <Select
                label="Framework"
                name="framework"
                value={framework}
                onChange={(event) => setFramework(event.target.value)}
                options={FRAMEWORK_OPTIONS}
                placeholder="Select a framework"
              />
            </div>
          </div>

          {/* ── Saved Topology ── */}
          <div>
            <SectionHeading>Environment</SectionHeading>
            <Select
              label="Saved Topologies"
              name="saved_topology"
              value={selectedTopologyId}
              onChange={(event) => setSelectedTopologyId(event.target.value)}
              options={topologyOptions}
              placeholder={
                isLoadingTopologies
                  ? "Loading saved topologies..."
                  : "Select a saved topology"
              }
            />
            {topologyLoadError && (
              <p className="mt-2 text-xs text-red-400">{topologyLoadError}</p>
            )}
          </div>

          {submitError && <p className="text-sm text-red-400">{submitError}</p>}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-(--color-border) pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-sm border border-(--color-border) px-4 py-2 text-sm font-semibold text-(--color-text-muted) transition hover:border-(--color-primary)/40 hover:text-(--color-primary) disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-sm border border-(--color-primary) bg-(--color-primary) px-4 py-2 text-sm font-semibold text-(--color-text-on-primary) transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Saving..." : "Create Scenario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}